import React, { FC, useState } from 'react'

import { Stack, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

// import { CustomButton } from '../../components/CustomButton'
import { EmptyAccountData } from '../../components/EmptyAccountData'
import { CustomModal } from '../../components/Modal'
import { CustomTabs, TabType } from '../../components/Tabs'
import { APP_PATHS } from '../../routes/paths'
import { useAppSelector } from '../../store/hooks'

import { CreateReportModalBody } from './CreateReportModalBody'
import { Drawdown } from './Drawdown'
import { Duration } from './Duration'
import { InstrumentsReports } from './Instruments'
import { PsychologyReports } from './Psychology'
import { RiskManagement } from './RiskManagement'
import { TimeReports } from './TimeReports'
import { TradingConfluenceReports } from './TradingConfluence'
import { TradingStrategyReports } from './TradingStrategy'
import { ReportsEnum } from './types'

const ReportsPage: FC = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { spacing, palette } = useTheme()
  const psychologyBadgeExpiresAt = new Date('2026-10-01T00:00:00+03:00')
  const showPsychologyNewBadge = new Date() < psychologyBadgeExpiresAt

  const navigate = useNavigate()
  const { userAccounts } = useAppSelector(({ userAccounts }) => userAccounts)

  const [activeTab, setActiveTab] = useState<number>(0)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [disableSubmitButton, setDisableSubmitButton] = useState(false)

  const reportTabs: TabType<ReportsEnum>[] = [
    {
      key: ReportsEnum.timeReport,
      label: t('timeReport', 'Time report'),
      component: <TimeReports />,
      className: 'tab-time-report',
    },
    {
      key: ReportsEnum.duration,
      label: t('duration', 'Duration'),
      component: <Duration />,
      className: 'tab-duration',
    },
    {
      key: ReportsEnum.instruments,
      label: t('instruments', 'Instruments'),
      component: <InstrumentsReports />,
      className: 'tab-instruments',
    },
    {
      key: ReportsEnum.psychology,
      label: t('psychology', 'Psychology'),
      badgeLabel: showPsychologyNewBadge ? (t('new', 'New') as string) : undefined,
      component: <PsychologyReports />,
      className: 'tab-psychology',
    },
    {
      key: ReportsEnum.tradingStrategy,
      label: t('tradingStrategy', 'Trading strategy'),
      component: <TradingStrategyReports />,
      className: 'tab-trading-strategy',
    },
    {
      key: ReportsEnum.tradingConfluence,
      label: t('tradingConfluence'),
      component: <TradingConfluenceReports />,
      className: 'tab-trading-confluence',
    },
    {
      key: ReportsEnum.riskManagement,
      label: t('riskManagement', 'Risk management'),
      component: <RiskManagement />,
      className: 'tab-risk-management',
    },
    {
      key: ReportsEnum.drawdown,
      label: t('drawdown', 'Drawdown'),
      component: <Drawdown />,
      className: 'tab-drawdown',
    },
  ]

  const toggleModal = () => setIsModalOpen((prevState) => !prevState)
  const createReport = () => {
    navigate(APP_PATHS.createReport)
    toggleModal()
  }

  return (
    <>
      <Stack height="100%" direction="column" flex={1} bgcolor={palette.background.default}>
        {Boolean(userAccounts.length) && (
          <Stack direction="column" m={spacing(2)} spacing={2}>
            <CustomTabs tabs={reportTabs} activeTab={activeTab} setActiveTab={setActiveTab} />

            <Box
              flex={1}
              sx={{
                minWidth: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              {reportTabs[activeTab].component}
            </Box>
          </Stack>
        )}
        {!Boolean(userAccounts.length) && <EmptyAccountData />}
      </Stack>

      <CustomModal
        open={isModalOpen}
        onClose={toggleModal}
        onSubmit={createReport}
        disableSubmitButton={disableSubmitButton}
        submitButtonText={t('generate', 'Generate') as string}
        modalBodyStyles={{
          width: spacing(56),
          maxWidth: '100vw',
          padding: spacing(2),
          boxSizing: 'border-box',
          [theme.breakpoints.down('sm')]: {
            width: `calc(100vw - ${spacing(4)})`,
            padding: spacing(2),
          },
        }}
        title={t('createReport', 'Create report')}
      >
        <CreateReportModalBody
          reportsList={reportTabs}
          setDisableSubmitButton={setDisableSubmitButton}
        />
      </CustomModal>

      {/* TODO: Implement normal report generation */}
      {/* {Boolean(userAccounts.length) && (
        <Fab
          onClick={toggleModal}
          size={'medium'}
          color={'primary'}
          sx={{ position: 'absolute', bottom: { xs: 85, md: 16 }, right: 16 }}
        >
          <AddchartOutlinedIcon />
        </Fab>
      )} */}
    </>
  )
}

export default ReportsPage
